import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { show as showExternalLinkModal } from '@/components/external-link-modal'
import Icon from '@/components/icon'
import { show as showVideoLightBox } from '@/components/video-light-box'
import { VideoLinkPlayer } from '@/components/video-player'
import { breakpoints } from '@/styles'
import embeddedLinkGenerators from '@/utils/video-embedded-links'
import VideoThumbnailMap, { DefaultThumbnail } from './video-thumbnails'
import VideoPreview from './video-preview'
import { UploadPlaceholder } from './placeholder-items'
import { show as showAddLinkModal } from './add-link-modal'

const StyledIcon = styled(Icon)`
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 50px;
    height: 35px;
  }
`

const supportedVideoProviders = {
  '3d': ['Eyexpo', 'YouTube', 'Matterport', 'Metareal'],
  '2d': ['YouTube', 'Vimeo']
}

const VideoLinkPreview = ({ linkData, onRemove, embedPlayer }) => {
  const [t] = useTranslation()

  const playbackUrl = embedPlayer
    ? embeddedLinkGenerators[linkData.link_service](linkData.link_url)
    : linkData.link_url
  const ThumbnailComponent = VideoThumbnailMap[linkData.link_service] || DefaultThumbnail

  const openLinkExternally = () => {
    showExternalLinkModal({
      url: linkData.link_url,
      message: t(
        'b.listing.edit.video_link.external_website.message',
        'Please note you will be redirected to a third-party website.'
      )
    })
  }

  const playVideoEmbedded = e => {
    showVideoLightBox({
      videoLocation: playbackUrl,
      controls: true,
      autoPlay: true,
      PlayerComponent: VideoLinkPlayer
    })
    e.stopPropagation()
  }

  const onPlay = embedPlayer ? playVideoEmbedded : openLinkExternally

  return (
    <VideoPreview onPlay={onPlay} onRemove={onRemove}>
      <ThumbnailComponent url={linkData.link_url} />
    </VideoPreview>
  )
}

const VideoLink = ({ listingId, videoType = '3d', onVideoLinkAddSuccess = () => {}, linkData }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const is3DVideo = videoType.toLowerCase() === '3d'

  const addVideoLink = async linkUrl => {
    const formattedUrl = linkUrl.replace(/\/shorts\//, '/embed/')
    const response = await dispatch.listing.addVideoLink({
      listingId,
      videoType,
      linkUrl: formattedUrl
    })

    if (response?.ok) {
      onVideoLinkAddSuccess()
    }

    return response.ok
  }

  const removeLink = async () => {
    await dispatch.listing.removeVideoLink({
      listingId,
      videoType
    })
  }

  const label = is3DVideo
    ? t('l.listing.edit.video_link.3d.label', 'Link 3D Video')
    : t('l.listing.edit.video_link.2d.label', 'Link Online Video')

  const supportedProviders = supportedVideoProviders[videoType] || supportedVideoProviders['2d']

  const modalTitle = t(
    'l.listing.edit.video_link.modal.title',
    'Paste the URL below to link your {{typePrefix}}video',
    { typePrefix: is3DVideo ? '3D ' : '' }
  )

  const modalProvidersMessage = is3DVideo
    ? t(
        'l.listing.edit.video_link.modal.virtual_tour.message',
        'Virtual tour and video service providers supported'
      )
    : t(
        'l.listing.edit.video_link.modal.video_providers.message',
        'Video service providers supported'
      )

  return linkData?.link_url ? (
    <VideoLinkPreview linkData={linkData} onRemove={removeLink} embedPlayer={!is3DVideo} />
  ) : (
    <UploadPlaceholder
      onClick={() =>
        showAddLinkModal({
          videoType,
          onAddLink: addVideoLink,
          supportedProviders,
          title: modalTitle,
          providersMessage: modalProvidersMessage
        })
      }
    >
      <StyledIcon id={is3DVideo ? 'threeD_video' : 'link'} width={47} height={55} />
      {label}
    </UploadPlaceholder>
  )
}

export default VideoLink
